import { Box, ButtonBase, IconButton, Tooltip } from '@mui/material';
import { CloseOutlined, EditOutlined } from '@mui/icons-material';
import { DocumentStatus } from '@synergy/models';
import { Form, Formik } from 'formik';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { ReactElement, ReactNode } from 'react';
import { ScrollToFieldError } from '@synergy/components';
import { connect } from 'react-redux';
import { padZeroLeft, sortByProperty } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';

import { AppState } from '../../../store/models/app-state';
import { Contact } from '../../../common/models/contact';
import { ContactDetails } from '../../../common/components/autocomplete-list/contact-details';
import {
    FormControlledStepComponent,
    FormControlledStepComponentProps,
} from '../../../common/components/abstract/form-controlled-step-component';
import { Inquiry } from '../models/inquiry';
import { Offer } from '../../../common/models/offer';
import { OfferStatus } from '../../../common/models/enums/offer-status';
import { PaloMeasure, paloSurplus } from '../../../common/components/palo/palo-measure';
import { PoolAccountContactAutocompleteList } from '../../../common/components/autocomplete-list/account-contact-autocomplete-list/pool-account-contact-autocomplete-list';
import { SUPPLIER_NAME_MAX_WIDTH, SUPPLIER_NAME_MIN_WIDTH, SupplierName } from '../utils/palo-supplier-name';
import { SupplierAccount } from '../models/supplier-account';
import { SupplierWithContact } from '../models/supplier-with-contact';
import { openInquiryOffers, setInquiryPickedSupplierCount } from '../palo/inquiry-dispatch-slot';

const c = tokens.color;

/** The tick box and the row menu as the list draws them. */
const SELECT_WIDTH = 46;
const CITY_WIDTH = 120;
/** Name left and cut, card and pencil at the right edge (R-191). */
const CONTACT_WIDTH = 250;
const STATUS_WIDTH = 150;
const OFFER_WIDTH = 80;

/** Where a supplier stands with this inquiry, in a word. */
type DispatchState = 'notSent' | 'sent' | 'submitted' | 'manual' | 'declined';

interface SupplierOfferListItem {
    /** The offer the sending refers to — that of the first delivery address, as before. */
    offerId?: number;
    id: number;
    name: string;
    city: string;
    supplierContact?: Contact;
    /** The supplier's own address for inquiries, which the mail goes to instead. */
    fixedRecipients?: string[];
    sendDate?: Date;
    state: DispatchState;
    /** The offer handed in, for the link. */
    submittedOfferId?: number;
}

interface InquiryDetailDispatchListProps extends FormControlledStepComponentProps<Partial<Inquiry>> {
    /** The offers of every delivery address of the inquiry. */
    offers: Offer[];
    /** Those of the first delivery address; the sending works on these. */
    firstOffers: Offer[];
    status?: DocumentStatus;
}

interface InquiryDetailDispatchListState {
    /** Rows whose contact is being changed: only those show the search field. */
    editingContacts: number[];
    selectedItems: number[];
    offerListItems: SupplierOfferListItem[];
}

interface InquiryDetailDispatchFromValues {
    supplierAccounts: SupplierAccount[];
}

/**
 * What of the offers matters here. The store hands out a new array on every
 * change anywhere, and a list that started over on each of them lost the
 * marks made in it.
 */
const offersKeyOf = (offers: Offer[]): string =>
    offers.map(offer => `${offer.id}:${offer.status}:${offer.emailSentDate ?? ''}`).join('|');

const emailOf = (contact?: Contact): string | undefined => contact?.email || contact?.emails?.[0]?.email;

/**
 * What the offers of one supplier say about him, over all delivery addresses
 * of the inquiry: one handed in is enough to count as an answer, the same as
 * the "Angebote x/y" in the list (#2260).
 */
const stateOf = (offers: Offer[]): Pick<SupplierOfferListItem, 'state' | 'sendDate' | 'submittedOfferId'> => {
    const handedIn = offers.find(
        offer =>
            OfferStatus.Pending === offer.status ||
            OfferStatus.Accepted === offer.status ||
            (OfferStatus.Declined === offer.status && !!offer.offerSubmittedBy)
    );
    const sendDate = offers
        .map(offer => offer.emailSentDate)
        .filter((date): date is Date => !!date)
        .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];

    if (handedIn) {
        // Entered by Pool Alpin rather than by the supplier: nobody was mailed.
        return { state: sendDate ? 'submitted' : 'manual', sendDate, submittedOfferId: handedIn.id };
    }

    if (offers.length > 0 && offers.every(offer => OfferStatus.Declined === offer.status)) {
        return { state: 'declined', sendDate };
    }

    if (sendDate) {
        return { state: 'sent', sendDate };
    }

    // Entered by hand without being handed in: the offer is still there to open (R-181).
    return offers.length > 0 ? { state: 'manual', submittedOfferId: offers[0].id } : { state: 'notSent' };
};

const PILL: Record<DispatchState, { color: string; background: string; border: string }> = {
    notSent: { color: c.warnInk, background: c.warnSoft, border: c.warnLine },
    sent: { color: c.ink2, background: c.surface, border: c.line },
    submitted: { color: c.okInk, background: c.okSoft, border: c.okLine },
    manual: { color: c.ink2, background: c.neutralSoft, border: c.line },
    declined: { color: c.crit, background: c.critSoft, border: c.critLine },
};

/**
 * Where the inquiry goes when the supplier has its own address for inquiries:
 * there, not to the contact, who is only named in the greeting (R-208, GF
 * 20.10.2026). Nothing when that address is the contact's own.
 */
const GoesTo = ({ recipients, contact }: { recipients?: string[]; contact?: Contact }): ReactElement | null => {
    const own = emailOf(contact)?.toLowerCase();
    const others = (recipients ?? []).filter(email => email.toLowerCase() !== own);

    if (0 === others.length) {
        return null;
    }

    const all = others.join(', ');

    return (
        <Box
            component="span"
            title={all}
            sx={{
                display: 'block',
                color: c.ink3,
                fontSize: '11.5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            <FormattedMessage id="app.inquiries.dispatch.goesTo" values={{ email: all }} />
        </Box>
    );
};

/** The state as a word, where a column of dates used to stand empty until something was sent. */
const DispatchStatePill = ({ item }: { item: SupplierOfferListItem }): ReactElement => {
    const look = PILL[item.state];
    let label: ReactNode = <FormattedMessage id={`app.inquiries.dispatch.state.${item.state}`} />;

    if ('sent' === item.state && item.sendDate) {
        label = (
            <>
                <FormattedMessage id="app.inquiries.dispatch.state.sentAt" />{' '}
                <FormattedDate value={item.sendDate} day="2-digit" month="2-digit" />{' '}
                <FormattedTime value={item.sendDate} />
            </>
        );
    }

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                padding: '1px 9px',
                fontSize: '11.5px',
                whiteSpace: 'nowrap',
                borderRadius: '999px',
                border: `1px solid ${look.border}`,
                color: look.color,
                backgroundColor: look.background,
            }}
        >
            {label}
        </Box>
    );
};

const unique = <T,>(items: T[], keyOf: (item: T) => unknown): T[] =>
    items.filter((item, index) => items.findIndex(other => keyOf(other) === keyOf(item)) === index);

/**
 * Who gets the inquiry, to which address, and where each of them stands.
 *
 * It stands at the top of "Lieferanten & Versand" and reads the suppliers from
 * the wizard's state, which the search below writes to the moment a supplier
 * is ticked — so a new one appears here without saving, and one taken off
 * here leaves the search's choice too (#2258).
 */
class InquiryDetailDispatchListBase extends FormControlledStepComponent<
    Partial<Inquiry>,
    InquiryDetailDispatchFromValues,
    InquiryDetailDispatchListProps,
    InquiryDetailDispatchListState
> {
    public state: InquiryDetailDispatchListState = {
        editingContacts: [],
        selectedItems: [],
        offerListItems: [],
    };

    public initialValues: InquiryDetailDispatchFromValues = {
        supplierAccounts: [],
    };

    /*
     * The supplier took everything the other columns left, a thousand pixels
     * of name next to a clipped place and status (R-190). It stops at the
     * width of the customer in the order list; what it would get beyond
     * that goes to place and status.
     */
    private columns = (editable: boolean, tableWidth: number): PaloColumn<SupplierOfferListItem>[] => {
        const fixed = (editable ? SELECT_WIDTH : 0) + CITY_WIDTH + CONTACT_WIDTH + STATUS_WIDTH + OFFER_WIDTH;
        const extra = paloSurplus(tableWidth, fixed, SUPPLIER_NAME_MAX_WIDTH, 2);

        return [
            {
                key: 'name',
                type: 'name',
                width: tableWidth
                    ? `${Math.max(SUPPLIER_NAME_MIN_WIDTH, Math.min(SUPPLIER_NAME_MAX_WIDTH, tableWidth - fixed))}px`
                    : undefined,
                head: <FormattedMessage id="app.table.column.supplier" />,
                cell: item => <SupplierName name={item.name} />,
            },
            {
                key: 'city',
                type: 'classification',
                width: `${CITY_WIDTH + extra}px`,
                head: <FormattedMessage id="app.inquiries.dispatch.city" />,
                cell: item => item.city,
            },
            {
                key: 'contact',
                type: 'name',
                width: `${CONTACT_WIDTH}px`,
                head: (
                    <>
                        <FormattedMessage id="app.table.column.contact" />
                        {editable ? ' *' : null}
                    </>
                ),
                cell: item => this.renderContact(item, editable),
            },
            {
                key: 'status',
                type: 'status',
                // "versendet 01.06. 15:42" in full (R-186).
                width: `${STATUS_WIDTH + extra}px`,
                head: <FormattedMessage id="app.table.column.status" />,
                cell: item => <DispatchStatePill item={item} />,
            },
            {
                key: 'offer',
                type: 'documentNumber',
                width: `${OFFER_WIDTH}px`,
                head: <FormattedMessage id="app.inquiries.dispatch.offer" />,
                cell: item =>
                    item.submittedOfferId ? (
                        <ButtonBase
                            onClick={openInquiryOffers}
                            sx={{ color: c.accentHi, fontSize: 'inherit', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {padZeroLeft(item.submittedOfferId, 4)}
                        </ButtonBase>
                    ) : (
                        // The column is set in the link colour; a dash is no link.
                        <Box component="span" sx={{ color: c.ink3 }}>
                            –
                        </Box>
                    ),
            },
        ];
    };

    /**
     * The contact by name, with the card that holds phone and mail behind 👤
     * and ✎ to pick another. The search field only where none is set or one
     * is being changed: a field per row made the list wider than the mask,
     * and a column of addresses repeated what the card shows (R-185).
     */
    private renderContact(item: SupplierOfferListItem, editable: boolean): ReactNode {
        const editing = this.state.editingContacts.includes(item.id);
        const contact = item.supplierContact;

        if (editable && (!contact || editing)) {
            const stopEditing = () =>
                this.setState(state => ({
                    editingContacts: state.editingContacts.filter(id => id !== item.id),
                }));

            /* No label of its own: the column already says "Ansprechperson"
               (#2214, R-40). A change can be called off with ✕, Esc or a
               click elsewhere, and the same person picked again is no change
               (R-187). It opens empty with every person of the supplier, the
               one so far marked: prefilled, the list showed only that one
               (R-192). */
            return (
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}
                    onKeyDown={event => {
                        if (editing && 'Escape' === event.key) {
                            stopEditing();
                        }
                    }}
                    onBlur={event => {
                        if (!editing) {
                            return;
                        }

                        // Later, so that a click into the list is taken first.
                        const box = event.currentTarget;
                        window.setTimeout(() => {
                            if (!box.contains(document.activeElement)) {
                                stopEditing();
                            }
                        }, 200);
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <PoolAccountContactAutocompleteList
                            accountId={item.id}
                            accountType="supplier"
                            label={<></>}
                            required={false}
                            value={editing ? undefined : contact}
                            currentContactId={editing ? contact?.id : undefined}
                            autoFocus={editing}
                            onSelected={selected => {
                                // An emptied field reports "none" when it loses
                                // focus, and a click into the list takes the
                                // focus first: ending the edit there dropped the
                                // click, the old person stayed (R-207). A row
                                // is never sent to no one; ✕ and Esc call off.
                                if (!selected) {
                                    return;
                                }

                                stopEditing();

                                if (selected?.id !== contact?.id) {
                                    this.changeContact(item)(selected);
                                }
                            }}
                        />
                    </Box>
                    {editing && contact && (
                        <Tooltip title={<FormattedMessage id="app.common.cancel" />}>
                            <IconButton size="small" onClick={stopEditing}>
                                <CloseOutlined sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            );
        }

        // The name takes the room and is cut, the marks stand at the right edge:
        // hanging on the name they moved with its length (R-191).
        return (
            <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                    <Box
                        component="span"
                        title={contact?.fullName}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {contact?.fullName ?? '–'}
                    </Box>
                    {contact?.id && <ContactDetails contactId={contact.id} accountId={item.id} />}
                    {editable && (
                        <Tooltip title={<FormattedMessage id="app.inquiries.dispatch.changeContact" />}>
                            <IconButton
                                size="small"
                                onClick={() =>
                                    this.setState(state => ({ editingContacts: [...state.editingContacts, item.id] }))
                                }
                            >
                                <EditOutlined sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {contact && !emailOf(contact) && (
                        <Box
                            component="span"
                            sx={{ color: c.crit, fontWeight: 600, whiteSpace: 'nowrap', fontSize: '11.5px' }}
                        >
                            <FormattedMessage id="app.inquiries.dispatch.noEmailShort" />
                        </Box>
                    )}
                </Box>
                <GoesTo recipients={item.fixedRecipients} contact={contact} />
            </Box>
        );
    }

    /** The contacts are handed on as they are picked, so there is nothing left to hand on here. */
    protected onSubmit = () => undefined;

    /** The suppliers as the wizard holds them now. */
    private supplierAccountsOf(props: InquiryDetailDispatchListProps): SupplierAccount[] {
        return props.globalState?.supplierAccounts ?? props.internalState?.supplierAccounts ?? [];
    }

    private handOn(supplierAccounts: SupplierAccount[]): void {
        this.props.propagateInternalState({ supplierAccounts });
    }

    private changeContact = (item: SupplierOfferListItem) => (contact: Contact | null) => {
        if (!contact) {
            return;
        }

        const supplierAccounts = this.supplierAccountsOf(this.props).map(supplierAccount => {
            if (supplierAccount.account.id !== item.id) {
                return supplierAccount;
            }
            const changed = SupplierAccount.fromPlainObject(supplierAccount);
            changed.contact = contact;

            return changed;
        });

        // Shown at once, before the wizard has passed the change back.
        const offerListItems = this.state.offerListItems.map(listItem =>
            listItem.id === item.id ? { ...listItem, supplierContact: contact } : listItem
        );
        this.setState({ offerListItems });
        this.updateCtaButton(offerListItems, this.state.selectedItems);
        this.handOn(supplierAccounts);
    };

    /** Only those not yet sent to and without an offer: the others are part of what happened. */
    private removable(): number[] {
        return this.state.offerListItems
            .filter(item => this.state.selectedItems.includes(item.id))
            .filter(item => !item.sendDate && undefined === item.offerId && 'notSent' === item.state)
            .map(item => item.id);
    }

    private removeSelected = () => {
        const removable = this.removable();

        this.handOn(
            this.supplierAccountsOf(this.props).filter(
                supplierAccount => !removable.includes(supplierAccount.account.id)
            )
        );
    };

    private onSelect = (selectedItems: number[]) => {
        this.setState({ selectedItems });
        this.updateCtaButton(this.state.offerListItems, selectedItems);
    };

    private getListItems(props: InquiryDetailDispatchListProps): SupplierOfferListItem[] {
        const supplierAccounts = this.supplierAccountsOf(props);
        const firstOfferOf = (supplierId: number) =>
            props.firstOffers.find(offer => offer.supplierAccount.id === supplierId);
        const offersOf = (supplierId: number) => props.offers.filter(offer => offer.supplierAccount.id === supplierId);

        const picked: SupplierOfferListItem[] = supplierAccounts.map(supplierAccount => {
            const supplier = supplierAccount.account;
            const offer = firstOfferOf(supplier.id);

            return {
                offerId: offer?.id,
                id: supplier.id,
                name: supplier.name,
                city: supplier.getCity?.() || '',
                supplierContact: supplierAccount.contact || offer?.supplierContact || supplier.getMainContact?.(),
                fixedRecipients: supplierAccount.fixedRecipientEmails,
                ...stateOf(offersOf(supplier.id)),
            };
        });

        // An offer whose supplier is no longer among the picked ones still
        // happened; it stays on the list.
        const pickedIds = picked.map(item => item.id);
        const others: SupplierOfferListItem[] = unique(
            props.offers.filter(offer => !pickedIds.includes(offer.supplierAccount.id)),
            offer => offer.supplierAccount.id
        ).map(offer => ({
            offerId: firstOfferOf(offer.supplierAccount.id)?.id,
            id: offer.supplierAccount.id,
            name: offer.supplierAccount.name,
            city: offer.supplierAccount.getCity() || '',
            supplierContact: offer.supplierContact || offer.supplierAccount.getMainContact(),
            ...stateOf(offersOf(offer.supplierAccount.id)),
        }));

        return [...picked, ...others];
    }

    /** Marked to be sent: who has had nothing yet. */
    private static isFresh(item: SupplierOfferListItem): boolean {
        return undefined === item.sendDate && undefined === item.offerId && 'notSent' === item.state;
    }

    private refresh(props: InquiryDetailDispatchListProps, keepSelection: boolean): void {
        const offerListItems = this.getListItems(props);
        const ids = offerListItems.map(item => item.id);
        const known = this.state.offerListItems.map(item => item.id);
        const selectedItems = keepSelection
            ? [
                  ...this.state.selectedItems.filter(id => ids.includes(id)),
                  // Ticked in the search: marked for sending straight away.
                  ...offerListItems
                      .filter(item => !known.includes(item.id) && InquiryDetailDispatchListBase.isFresh(item))
                      .map(item => item.id),
              ]
            : offerListItems.filter(InquiryDetailDispatchListBase.isFresh).map(item => item.id);

        this.setState({ selectedItems, offerListItems });
        setInquiryPickedSupplierCount(offerListItems.length);
        this.updateCtaButton(offerListItems, selectedItems);
    }

    private updateCtaButton = (offerListItems: SupplierOfferListItem[], selectedItems: number[]) => {
        const dispatchItems = offerListItems
            .filter(listItem => selectedItems.includes(listItem.id))
            .map<SupplierWithContact>(listItem => ({
                offerId: listItem.offerId,
                id: listItem.id,
                supplierContact: listItem.supplierContact,
            }));

        // A marked supplier without an address would get nothing: no sending
        // until each one has one.
        const ctaButtonEnabled =
            dispatchItems.length > 0 && dispatchItems.every(item => !!emailOf(item.supplierContact));
        this.props.onCtaButtonChanged?.(ctaButtonEnabled, dispatchItems);
    };

    public componentDidMount() {
        this.refresh(this.props, false);
    }

    public componentWillUnmount() {
        setInquiryPickedSupplierCount(0);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: InquiryDetailDispatchListProps) {
        const offersChanged = offersKeyOf(this.props.offers) !== offersKeyOf(nextProps.offers);

        if (!offersChanged && this.supplierAccountsOf(this.props) === this.supplierAccountsOf(nextProps)) {
            return;
        }

        // Sent: the server's view is the new start, marked as on opening.
        // Otherwise the selection made here stays.
        this.refresh(nextProps, !offersChanged);
    }

    private renderHead(readonly: boolean) {
        const { offerListItems, selectedItems } = this.state;
        const sent = offerListItems.filter(item => !!item.sendDate).length;
        const offers = offerListItems.filter(item => 'submitted' === item.state || 'manual' === item.state).length;
        const { endDate, enableExpirationNotifications, dueDate } = this.props.globalState ?? {};

        return (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', mb: '8px' }}>
                <Box
                    sx={{
                        fontSize: '10.5px',
                        fontWeight: 640,
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        color: c.ink3,
                    }}
                >
                    <FormattedMessage id="app.inquiries.dispatch.title" />
                </Box>
                <Box sx={{ fontSize: '12.5px', color: c.ink2 }}>
                    <FormattedMessage
                        id={readonly ? 'app.inquiries.dispatch.summaryReadonly' : 'app.inquiries.dispatch.summary'}
                        values={{
                            suppliers: offerListItems.length,
                            marked: selectedItems.length,
                            sent,
                            offers,
                        }}
                    />
                </Box>
                <Box sx={{ marginLeft: 'auto', fontSize: '12.5px', color: c.ink2 }}>
                    {endDate && (
                        <>
                            <FormattedMessage id="app.inquiries.dispatch.deadline" />{' '}
                            <FormattedDate value={endDate} day="2-digit" month="2-digit" year="numeric" />
                            {' · '}
                        </>
                    )}
                    {enableExpirationNotifications && dueDate ? (
                        <>
                            <FormattedMessage id="app.inquiries.dispatch.reminderOn" />{' '}
                            <FormattedDate value={dueDate} day="2-digit" month="2-digit" />
                        </>
                    ) : (
                        <FormattedMessage id="app.inquiries.dispatch.reminderOff" />
                    )}
                </Box>
            </Box>
        );
    }

    private renderFoot() {
        const { offerListItems, selectedItems } = this.state;
        const withoutEmail = offerListItems.filter(
            item => selectedItems.includes(item.id) && !emailOf(item.supplierContact)
        ).length;
        const removable = this.removable();

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '6px 2px 0',
                    fontSize: '11.5px',
                    color: c.ink3,
                }}
            >
                <span>
                    <FormattedMessage id="app.inquiries.dispatch.markedHint" />
                </span>
                {withoutEmail > 0 && (
                    <Box component="span" sx={{ color: c.crit, fontWeight: 600 }}>
                        <FormattedMessage id="app.inquiries.dispatch.blockedNoEmail" values={{ count: withoutEmail }} />
                    </Box>
                )}
                <ButtonBase
                    disabled={0 === removable.length}
                    onClick={this.removeSelected}
                    sx={{
                        marginLeft: 'auto',
                        fontSize: '12px',
                        color: removable.length > 0 ? c.accentHi : c.ink3,
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    <FormattedMessage id="app.inquiries.dispatch.remove" />
                </ButtonBase>
            </Box>
        );
    }

    private renderTable(readonly: boolean) {
        const { offerListItems, selectedItems } = this.state;
        const ids = offerListItems.map(item => item.id);

        return (
            <PaloMeasure>
                {tableWidth => (
                    <PaloListView<SupplierOfferListItem, number>
                        embedded={true}
                        columns={this.columns(!readonly, tableWidth)}
                        items={offerListItems}
                        getRowId={item => item.id}
                        emptyLabel={<FormattedMessage id="app.inquiries.dispatch.empty" />}
                        selection={
                            readonly
                                ? undefined
                                : {
                                      selectedIds: selectedItems,
                                      allSelected: ids.length > 0 && ids.every(id => selectedItems.includes(id)),
                                      someSelected: ids.some(id => selectedItems.includes(id)),
                                      onToggle: id =>
                                          this.onSelect(
                                              selectedItems.includes(id)
                                                  ? selectedItems.filter(selectedId => selectedId !== id)
                                                  : [...selectedItems, id]
                                          ),
                                      onToggleAll: () =>
                                          this.onSelect(ids.every(id => selectedItems.includes(id)) ? [] : ids),
                                  }
                        }
                    />
                )}
            </PaloMeasure>
        );
    }

    public render() {
        const { unlocked, globalState } = this.props;

        const readonly =
            (!unlocked &&
                (DocumentStatus.Completed === globalState?.status ||
                    DocumentStatus.CompletedWithTransition === globalState?.status ||
                    DocumentStatus.CompletedWithoutTransition === globalState?.status)) ||
            false;

        return (
            <Formik
                initialValues={this.initialValues}
                onSubmit={this.onSubmit}
                isInitialValid={true}
                innerRef={ref => (this.formikInstance = ref)}
            >
                {() => (
                    <Form>
                        <ScrollToFieldError />
                        {this.renderHead(readonly)}
                        {this.renderTable(readonly)}
                        {!readonly && this.renderFoot()}
                    </Form>
                )}
            </Formik>
        );
    }
}

const mapStateToProps = (state: AppState) => {
    const accountPurchases = state.inquiries.entity?.accountPurchases ?? [];

    return {
        status: state.inquiries.entity && state.inquiries.entity.status,
        offers: sortByProperty({
            data: accountPurchases.flatMap(accountPurchase => accountPurchase.offers ?? []),
            key: 'id',
        }),
        firstOffers: sortByProperty({ data: accountPurchases[0]?.offers ?? [], key: 'id' }),
    };
};

export const InquiryDetailDispatchList = connect(mapStateToProps, {})(InquiryDetailDispatchListBase);
